import { useEffect, useState, useRef } from "react";
import { MagnifyingGlassIcon, Cross2Icon } from "@radix-ui/react-icons";
import { Button, TextField, IconButton, Flex } from "@radix-ui/themes";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import { useFormTracking } from "@/hooks/useFormTracking";

export const Search = ({
  setSearchPhrase,
  placeholder,
  initialValue,
  debounce = 0,
  context = "unknown",
  size = "1",
}: {
  setSearchPhrase: (value: string) => void;
  placeholder: string;
  initialValue?: string;
  debounce?: number;
  context?: string;
  size?: "1" | "2" | "3";
}) => {
  const [isSearchActive, setSearchActive] = useState(
    initialValue ? true : false
  );
  const [value, setValue] = useState(initialValue || "");
  const { trackSearch } = useFormTracking();
  const searchRef = useRef<HTMLDivElement>(null);
  const debouncedSetSearchPhrase = useDebouncedCallback(
    setSearchPhrase,
    debounce
  );

  useEffect(() => {
    debouncedSetSearchPhrase(value);
    if (value.trim()) {
      trackSearch(value, context);
    }
  }, [value, trackSearch, context]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && isSearchActive) {
        setSearchActive(false);
        setValue("");
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isSearchActive]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        isSearchActive &&
        searchRef.current &&
        !searchRef.current.contains(e.target as Node) &&
        value === ""
      ) {
        setSearchActive(false);
      }
    }

    if (isSearchActive) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isSearchActive, value]);

  const handleClear = () => {
    setValue("");
    setSearchPhrase("");
  };

  return (
    <div ref={searchRef}>
      {isSearchActive ? (
        <Flex gap="1" align="center">
          <TextField.Root
            placeholder={placeholder}
            size={size}
            autoFocus
            variant="soft"
            color="gray"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          >
            <TextField.Slot>
              <MagnifyingGlassIcon height="16" width="16" />
            </TextField.Slot>
          </TextField.Root>
          {value && (
            <IconButton
              variant="ghost"
              color="gray"
              size={size}
              onClick={handleClear}
              aria-label="Clear search"
            >
              <Cross2Icon width={14} height={14} />
            </IconButton>
          )}
        </Flex>
      ) : (
        <Button
          color="gray"
          variant="soft"
          highContrast
          size={size}
          onClick={() => setSearchActive(true)}
        >
          <MagnifyingGlassIcon /> Find by
        </Button>
      )}
    </div>
  );
};
